import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Upload,
  FileText,
  Trash2,
  Eye,
  Search,
  CheckCircle,
  Clock,
  AlertCircle,
  X
} from 'lucide-react'
import { useDropzone } from 'react-dropzone'
import { toast } from 'react-toastify'
import DashboardLayout from '@/components/DashboardLayout'
import GlassCard from '@/components/ui/GlassCard'
import GradientButton from '@/components/ui/GradientButton'
import api from '@/services/api'

interface Document {
  id: string
  title: string
  file_type: string
  content: string
  summary: string
  keywords: string[]
  file_size: number
  processing_status: string
  is_active: boolean
  upload_date: string
}

const DocumentsPage: React.FC = () => {
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadTitle, setUploadTitle] = useState('')

  useEffect(() => {
    fetchDocuments()
  }, [])

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/api/documents')
      setDocuments(response.data)
    } catch (error) {
      console.error('Error fetching documents:', error)
      toast.error('Error al cargar documentos')
    } finally {
      setLoading(false)
    }
  }

  const onDrop = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0]
    if (!file) return

    setUploading(true)
    const formData = new FormData()
    formData.append('document', file)
    formData.append('title', uploadTitle || file.name)

    try {
      await api.post('/api/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      
      toast.success('✅ Documento procesado exitosamente')
      await fetchDocuments()
      setShowUploadModal(false)
      setUploadTitle('')
    } catch (error: any) {
      console.error('Error uploading document:', error)
      
      if (error.response?.data?.error === 'plan_limit_reached') {
        toast.error(`❌ Límite de documentos excedido para tu plan ${error.response.data.current_plan.toUpperCase()} (${error.response.data.current_usage}/${error.response.data.limit})`)
      } else if (error.response?.data?.error === 'file_size_limit_reached') {
        toast.error('❌ Archivo demasiado grande para tu plan')
      } else {
        toast.error('❌ Error al subir documento')
      }
    } finally {
      setUploading(false)
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf']
    },
    maxSize: 10485760,
    multiple: false
  })

  const deleteDocument = async (id: string) => {
    if (window.confirm('⚠️ ¿Eliminar PERMANENTEMENTE este documento? Esta acción no se puede deshacer.')) {
      try {
        await api.delete(`/api/documents/${id}`)
        toast.success('✅ Documento eliminado permanentemente')
        await fetchDocuments()
      } catch (error) {
        console.error('Error deleting document:', error)
        toast.error('❌ Error al eliminar documento')
      }
    }
  }

  const toggleDocumentStatus = async (id: string, currentStatus: boolean) => {
    try {
      await api.put(`/api/documents/${id}`, { is_active: !currentStatus })
      toast.success(currentStatus ? '✅ Documento desactivado' : '✅ Documento activado')
      await fetchDocuments()
    } catch (error) {
      console.error('Error updating document status:', error)
      toast.error('❌ Error al cambiar estado')
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-400" />
      case 'processing':
        return <Clock className="h-5 w-5 text-yellow-400 animate-spin" />
      case 'error':
        return <AlertCircle className="h-5 w-5 text-red-400" />
      default:
        return <FileText className="h-5 w-5 text-white/60" />
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Procesado'
      case 'processing':
        return 'Procesando'
      case 'error':
        return 'Error'
      default:
        return 'Pendiente'
    }
  }

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.keywords.some(keyword => keyword.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesStatus = filterStatus === 'all' ||
                         (filterStatus === 'active' && doc.is_active) ||
                         (filterStatus === 'inactive' && !doc.is_active) ||
                         (filterStatus === doc.processing_status)
    return matchesSearch && matchesStatus
  })

  return (
    <DashboardLayout activeSection="documents">
      <div className="space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
        >
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Documentos de Entrenamiento</h1>
            <p className="text-white/70">Gestiona los documentos que entrenan a tu chatbot IA</p>
          </div>
          <GradientButton onClick={() => setShowUploadModal(true)}>
            <Upload className="h-5 w-5 mr-2" />
            Subir Documento
          </GradientButton>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4"
        >
          <GlassCard>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{documents.length}</div>
              <div className="text-white/60 text-sm">Total Documentos</div>
            </div>
          </GlassCard>
          <GlassCard>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">
                {documents.filter(d => d.is_active).length}
              </div>
              <div className="text-white/60 text-sm">Activos</div>
            </div>
          </GlassCard>
          <GlassCard>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">
                {documents.filter(d => d.processing_status === 'completed').length}
              </div>
              <div className="text-white/60 text-sm">Procesados</div>
            </div>
          </GlassCard>
          <GlassCard>
            <div className="text-center">
              <div className="text-2xl font-bold text-white">
                {formatFileSize(documents.reduce((sum, doc) => sum + doc.file_size, 0))}
              </div>
              <div className="text-white/60 text-sm">Tamaño Total</div>
            </div>
          </GlassCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <GlassCard>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
                <input
                  type="text"
                  placeholder="Buscar documentos o keywords..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl bg-white/10 border border-white/20 py-3 pl-10 pr-4 text-white placeholder-white/50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="all" className="bg-gray-900 text-white">Todos los estados</option>
                <option value="active" className="bg-gray-900 text-white">Activos</option>
                <option value="inactive" className="bg-gray-900 text-white">Inactivos</option>
                <option value="completed" className="bg-gray-900 text-white">Procesados</option>
                <option value="error" className="bg-gray-900 text-white">Con errores</option>
              </select>
            </div>
          </GlassCard>
        </motion.div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <GlassCard>
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-white/10 rounded-lg"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-white/10 rounded mb-2"></div>
                      <div className="h-3 bg-white/10 rounded w-2/3"></div>
                    </div>
                  </div>
                </GlassCard>
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            <AnimatePresence>
              {filteredDocuments.map((document, index) => (
                <motion.div
                  key={document.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                  layout
                >
                  <GlassCard hover className="group">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="h-12 w-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                          <FileText className="h-6 w-6 text-white" />
                        </div>
                        <div className="absolute -top-1 -right-1">
                          {getStatusIcon(document.processing_status)}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-white font-semibold truncate">
                            {document.title}
                          </h3>
                          <span className={`
                            px-2 py-1 rounded-full text-xs font-medium
                            ${document.is_active
                              ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                              : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                            }
                          `}>
                            {document.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </div>

                        <div className="flex items-center gap-4 text-white/60 text-sm">
                          <span>{getStatusText(document.processing_status)}</span>
                          <span>{formatFileSize(document.file_size)}</span>
                          <span>{new Date(document.upload_date).toLocaleDateString()}</span>
                        </div>

                        {document.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {document.keywords.slice(0, 3).map((keyword, i) => (
                              <span
                                key={i}
                                className="px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full"
                              >
                                {keyword}
                              </span>
                            ))}
                            {document.keywords.length > 3 && (
                              <span className="px-2 py-1 bg-white/10 text-white/60 text-xs rounded-full">
                                +{document.keywords.length - 3} más
                              </span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setSelectedDocument(document)}
                          className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                          title="Ver contenido"
                        >
                          <Eye className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => toggleDocumentStatus(document.id, document.is_active)}
                          className={`
                            p-2 rounded-lg transition-colors
                            ${document.is_active
                              ? 'text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10'
                              : 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                            }
                          `}
                          title={document.is_active ? 'Desactivar' : 'Activar'}
                        >
                          {document.is_active ? (
                            <X className="h-4 w-4" />
                          ) : (
                            <CheckCircle className="h-4 w-4" />
                          )}
                        </button>

                        <button
                          onClick={() => deleteDocument(document.id)}
                          className="p-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                          title="Eliminar permanentemente"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {!loading && filteredDocuments.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12"
          >
            <GlassCard>
              <div className="py-8">
                <FileText className="h-16 w-16 text-white/30 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  {searchTerm || filterStatus !== 'all' ? 'No se encontraron documentos' : 'No tienes documentos aún'}
                </h3>
                <p className="text-white/60 mb-6">
                  {searchTerm || filterStatus !== 'all'
                    ? 'Intenta ajustar tus filtros de búsqueda'
                    : 'Sube documentos para entrenar tu chatbot IA con información específica'
                  }
                </p>
                {!searchTerm && filterStatus === 'all' && (
                  <GradientButton onClick={() => setShowUploadModal(true)}>
                    <Upload className="h-5 w-5 mr-2" />
                    Subir Primer Documento
                  </GradientButton>
                )}
              </div>
            </GlassCard>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setShowUploadModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="w-full max-w-lg"
            >
              <GlassCard>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-white">Subir Documento</h2>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="text-white/60 hover:text-white transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-white/70 text-sm font-medium mb-2">
                      Título del documento (opcional)
                    </label>
                    <input
                      value={uploadTitle}
                      onChange={(e) => setUploadTitle(e.target.value)}
                      placeholder="Ej: Manual de Procedimientos"
                      className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    />
                  </div>

                  <div
                    {...getRootProps()}
                    className={`
                      border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                      ${isDragActive
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-white/30 hover:border-blue-500/50 hover:bg-white/5'
                      }
                    `}
                  >
                    <input {...getInputProps()} />

                    {uploading ? (
                      <div className="space-y-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                        <p className="text-white">Procesando documento...</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Upload className="h-12 w-12 text-white/60 mx-auto" />
                        <div>
                          <p className="text-white mb-2">
                            {isDragActive
                              ? 'Suelta el archivo aquí...'
                              : 'Arrastra un archivo aquí o haz clic para seleccionar'
                            }
                          </p>
                          <p className="text-white/60 text-sm">
                            Soporta: TXT, PDF (máx. según tu plan)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selectedDocument && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && setSelectedDocument(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            >
              <GlassCard>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-white">{selectedDocument.title}</h2>
                  <button
                    onClick={() => setSelectedDocument(null)}
                    className="text-white/60 hover:text-white transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {selectedDocument.summary && (
                    <div>
                      <h3 className="text-white font-semibold mb-2">Resumen</h3>
                      <p className="text-white/70 bg-white/5 rounded-lg p-4">
                        {selectedDocument.summary}
                      </p>
                    </div>
                  )}

                  <div>
                    <h3 className="text-white font-semibold mb-2">Contenido</h3>
                    <div className="bg-white/5 rounded-lg p-4 max-h-96 overflow-y-auto">
                      <pre className="text-white/70 whitespace-pre-wrap font-mono text-sm">
                        {selectedDocument.content}
                      </pre>
                    </div>
                  </div>

                  {selectedDocument.keywords.length > 0 && (
                    <div>
                      <h3 className="text-white font-semibold mb-2">Keywords</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedDocument.keywords.map((keyword, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-blue-500/20 text-blue-300 text-sm rounded-full border border-blue-500/30"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </GlassCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardLayout>
  )
}

export default DocumentsPage
